/**
 * Leave Approval — compact desktop approval workspace (LEAVE-UI-REDESIGN)
 * Categories: Pending Leave Requests / Cancellation Requests / On-Behalf Requests
 * All backend workflow, scoping, maker-checker and audit logic unchanged.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Calendar,
  Eye,
  Loader2,
  Ban,
  Undo2,
  Info,
  UserPlus,
  Paperclip,
  Lock,
  FileText,
  X,
  History,
  ShieldCheck
} from 'lucide-react';
import { formatCalendarDate, formatDateOnly, formatDateTimeLocal } from '../../utils/formatters';
import { OnBehalfLeaveDialog } from './leave/OnBehalfLeaveDialog';
import { ApprovalTabs, FilterBar, PaginationFooter, TableSkeleton, Avatar, useTabStates, applyFilters, paginate, DEFAULT_TAB_STATE } from './leave/ApprovalsToolkit';
import { ApprovedLeavesTab } from './leave/ApprovedLeavesTab';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LEAVE_TYPE_CONFIG = {
  casual: { label: 'Casual' },
  sick: { label: 'Sick' },
  planned: { label: 'Planned' },
  emergency: { label: 'Emergency' },
  earned: { label: 'Earned' },
  compensatory: { label: 'Compensatory' },
  maternity: { label: 'Maternity' },
  unpaid: { label: 'Loss of Pay' }
};

const leaveTypeLabel = (type) =>
  LEAVE_TYPE_CONFIG[type]?.label ||
  (type || 'Leave').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  return formatDateOnly(dateStr);
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '--';
  return formatDateTimeLocal(dateStr);
};

const dayPartLabel = (dp) =>
  !dp ? '—' : dp === 'half_am' ? 'Half Day — Morning' : dp === 'half_pm' ? 'Half Day — Afternoon' : 'Full Day';

const weekdaySpan = (startStr, endStr) => {
  try {
    const s = new Date(startStr + 'T00:00:00');
    const e = new Date(endStr + 'T00:00:00');
    let cal = 0, wk = 0;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      cal += 1;
      if (d.getDay() !== 0 && d.getDay() !== 6) wk += 1;
    }
    return { cal, wk };
  } catch {
    return { cal: 0, wk: 0 };
  }
};

const shortRange = (startStr, endStr) => {
  const s = formatCalendarDate(startStr);
  const e = formatCalendarDate(endStr);
  return startStr === endStr ? s : `${s} – ${e}`;
};

const LeaveApproval = ({ currentUser, openOnBehalfSignal = 0, onCountsChanged = null }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const limit = 100;

  // decision modals
  const [commentModal, setCommentModal] = useState(null);   // { request, action: 'approve' }
  const [rejectModal, setRejectModal] = useState(null);
  const [comment, setComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // cancellation queue + history
  const [cancellationRequests, setCancellationRequests] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [updatesTotal, setUpdatesTotal] = useState(0);
  const [showAllUpdates, setShowAllUpdates] = useState(false);
  const [cancelActionLoading, setCancelActionLoading] = useState(null);
  const [confirmCancelApprove, setConfirmCancelApprove] = useState(null);
  const [rejectCancelModal, setRejectCancelModal] = useState(null);
  const [rejectCancelReason, setRejectCancelReason] = useState('');

  // details drawer
  const [detailsItem, setDetailsItem] = useState(null); // { kind: 'pending'|'cancellation', data }

  // on-behalf submission
  const [onBehalfOpen, setOnBehalfOpen] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);
  // LEAVE-APPROVALS-UI-002: real tabs + per-tab filter/pagination state
  const [activeTab, setActiveTab] = useState('pending');
  const [tabStates, updateTab] = useTabStates(['pending', 'cancellation', 'onbehalf']);
  const [banner, setBanner] = useState(null);
  // LEAVE-APPROVED-VIEW-001 / UI-CONSISTENCY-002: total approved count in scope shown on the tab badge (read-only)
  const [approvedCount, setApprovedCount] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/leave-registry/reports/requests`, { params: { status: 'approved', view: 'approvals', window: 'all', page: 1, page_size: 1 } })
      .then(res => setApprovedCount(res.data?.total ?? 0)).catch(() => setApprovedCount(0));
  }, []);

  const fetchPendingRequests = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const fetchAllPending = async () => {
        const first = await axios.get(`${API}/api/leave/pending-approvals`, { params: { page: 1, limit } });
        let rows = first.data.requests || [];
        const totalRows = first.data.total || rows.length;
        for (let page = 2; rows.length < totalRows && page <= 10; page++) {
          const more = await axios.get(`${API}/api/leave/pending-approvals`, { params: { page, limit } });
          rows = rows.concat(more.data.requests || []);
        }
        return { requests: rows, total: totalRows };
      };
      const [response, mineRes] = await Promise.all([
        fetchAllPending(),
        axios.get(`${API}/api/leave/on-behalf/my-submissions`, { params: { page: 1, limit: 500 } }).catch(() => null),
      ]);
      setRequests(response.requests);
      setTotal(response.total);
      setMySubmissions(mineRes?.data?.requests || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching pending approvals:', err);
      setError('Failed to load pending requests');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchCancellationData = useCallback(async (all = false) => {
    try {
      const [reqRes, updRes] = await Promise.all([
        axios.get(`${API}/api/leave/cancellation-requests`),
        axios.get(`${API}/api/leave/recent-updates`, { params: { days: all ? 365 : 30, limit: all ? 50 : 5 } })
      ]);
      setCancellationRequests(reqRes.data?.requests || []);
      setRecentUpdates(updRes.data?.updates || []);
      setUpdatesTotal(updRes.data?.total || 0);
    } catch (err) {
      console.error('Failed to load cancellation data', err);
    }
  }, []);

  useEffect(() => { fetchPendingRequests(); }, [fetchPendingRequests]);
  useEffect(() => { fetchCancellationData(); }, [fetchCancellationData]);
  useEffect(() => {
    if (openOnBehalfSignal > 0) setOnBehalfOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOnBehalfSignal]);

  const pendingSelf = useMemo(() => requests.filter(r => !r.submitted_on_behalf), [requests]);
  // LEAVE-ONBEHALF-003: decidable on-behalf requests + full tracking history of
  // the caller's own submissions (any status), deduped by id
  const onBehalfReqs = useMemo(() => {
    const merged = new Map();
    requests.filter(r => r.submitted_on_behalf).forEach(r => merged.set(r.id, r));
    mySubmissions.forEach(r => { if (!merged.has(r.id)) merged.set(r.id, r); });
    return [...merged.values()].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [requests, mySubmissions]);
  const isDecidableStatus = (r) => r.status === 'pending' || r.status === 'pending_hr';

  const showBanner = (msg) => {
    setBanner(msg);
    setTimeout(() => setBanner(null), 8000);
  };

  const handleAction = async (requestId, action) => {
    if (actionLoading) return;
    try {
      setActionLoading(requestId);
      const payload = action === 'reject'
        ? { rejection_reason: rejectionReason, comments: comment || null }
        : { comments: comment || null };
      await axios.put(`${API}/api/leave/${action}/${requestId}`, payload);
      setRequests(prev => prev.filter(r => r.id !== requestId));
      setTotal(prev => Math.max(0, prev - 1));
      showBanner(action === 'approve' ? 'Leave request approved successfully.' : 'Leave request rejected.');
      fetchPendingRequests(true);
      setCommentModal(null);
      setRejectModal(null);
      setComment('');
      setRejectionReason('');
      setDetailsItem(null);
      fetchCancellationData(showAllUpdates);
      if (onCountsChanged) onCountsChanged();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancellationDecision = async (requestId, decision, reason) => {
    if (cancelActionLoading) return;
    try {
      setCancelActionLoading(requestId);
      const payload = decision === 'reject' ? { rejection_reason: reason?.trim() || null } : {};
      await axios.put(`${API}/api/leave/cancellation/${requestId}/${decision}`, payload);
      setCancellationRequests(prev => prev.filter(r => r.id !== requestId));
      setConfirmCancelApprove(null);
      setRejectCancelModal(null);
      setRejectCancelReason('');
      setDetailsItem(null);
      fetchCancellationData(showAllUpdates);
      if (onCountsChanged) onCountsChanged();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${decision} cancellation request`);
    } finally {
      setCancelActionLoading(null);
    }
  };

  const onBehalfSubmitted = ({ employeeName, docWarning }) => {
    toast.success(`Leave request submitted successfully for ${employeeName}. Pending HR/Admin approval.${docWarning}`, { duration: 6000 });
    showBanner(`Leave submitted for ${employeeName} — pending HR/Admin approval.${docWarning}`);
    fetchPendingRequests(true);
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await axios.get(`${API}/api/leave-registry/documents/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const isSubmitter = (r) => r.submitted_on_behalf && r.on_behalf_submitted_by_id === currentUser?.id;
  // LEAVE-ONBEHALF-002 — mirrors the backend guard (source of truth):
  // own personal leave: never decidable; on-behalf: admin always (incl. own
  // submissions), hr_manager except own submissions, others never.
  const canDecide = (r) => {
    if (r.user_id === currentUser?.id) return false;
    if (!r.submitted_on_behalf) return true;
    if (currentUser?.role === 'admin') return true;
    if (currentUser?.role === 'hr_manager') return !isSubmitter(r);
    return false;
  };
  const lockReason = (r) =>
    r.user_id === currentUser?.id
      ? 'You cannot approve or reject your own leave request — another authorized approver must decide it'
      : 'Maker-checker: you submitted this request — another HR Manager/Admin must decide it';

  const renderActions = (r) => (
    <div className="flex items-center gap-1 justify-end">
      <button
        onClick={() => setDetailsItem({ kind: 'pending', data: r })}
        data-testid={`view-details-btn-${r.id}`}
        title="View Details"
        className="la-action p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100"
      >
        <Eye className="w-4 h-4" />
      </button>
      {!isDecidableStatus(r) ? null : !canDecide(r) ? (
        <span data-testid={`maker-checker-note-${r.id}`} title={lockReason(r)}
          className="p-1.5 text-slate-400 cursor-not-allowed">
          <Lock className="w-4 h-4" />
        </span>
      ) : (
        <>
          <button
            onClick={() => { setCommentModal({ request: r, action: 'approve' }); setComment(''); }}
            disabled={actionLoading === r.id}
            data-testid={`approve-btn-${r.id}`}
            title="Approve"
            className="la-action p-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setRejectModal(r); setRejectionReason(''); setComment(''); }}
            disabled={actionLoading === r.id}
            data-testid={`reject-btn-${r.id}`}
            title="Reject"
            className="la-action p-1.5 rounded-lg text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* LEAVE-APPROVALS-UI-002 — tabs */}
      <ApprovalTabs active={activeTab} onChange={setActiveTab} tabs={[
        { key: 'pending', label: 'Pending Leave Requests', icon: Clock, tone: 'blue', count: pendingSelf.length },
        { key: 'cancellation', label: 'Cancellation Requests', icon: Ban, tone: 'amber', count: cancellationRequests.length },
        { key: 'onbehalf', label: 'On-Behalf Requests', icon: UserPlus, tone: 'violet', count: onBehalfReqs.length },
        { key: 'approved', label: 'Approved Leaves', icon: ShieldCheck, tone: 'emerald', count: approvedCount ?? 0 },
      ]} />

      {banner && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-4 py-3 text-sm la-panel" data-testid="on-behalf-success-banner">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {banner}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-800">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {activeTab === 'approved' && <ApprovedLeavesTab onCount={setApprovedCount} />}

      {activeTab !== 'approved' && (() => {
        const tab = activeTab;
        const st = tabStates[tab];
        const tone = tab === 'pending' ? 'blue' : tab === 'cancellation' ? 'amber' : 'violet';
        const source = tab === 'pending' ? pendingSelf : tab === 'cancellation' ? cancellationRequests : onBehalfReqs;
        const typeOptions = [...new Set(source.map(r => r.leave_type).filter(Boolean))].map(t => [t, leaveTypeLabel(t)]);
        const statusOptions = tab === 'pending' ? [['pending', 'Pending'], ['pending_hr', 'HR stage']]
          : tab === 'cancellation' ? [['started', 'Leave started'], ['upcoming', 'Not started']]
            : [['pending_hr', 'Pending HR/Admin'], ['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected'], ['cancelled', 'Cancelled']];
        const filtered = applyFilters(source, st, {
          getDates: r => [r.start_date, r.end_date],
          getStatus: tab === 'cancellation' ? (r => (r.leave_started ? 'started' : 'upcoming')) : (r => r.status),
          getText: r => `${r.user_full_name || ''} ${r.reason || ''} ${r.cancellation_reason || ''} ${leaveTypeLabel(r.leave_type)} ${r.on_behalf_submitted_by_name || ''}`,
        });
        const { page, totalPages, slice } = paginate(filtered, st);
        const hasFilters = st.search || st.type !== 'all' || st.date !== 'all' || st.status !== 'all';
        const panelKey = `${tab}-${page}-${st.pageSize}-${st.search}-${st.type}-${st.date}-${st.from}-${st.to}-${st.status}`;
        const cols = tab === 'onbehalf' ? 9 : 7;
        const statusChip = (r) => (
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
            r.status === 'approved' ? 'bg-emerald-100 text-emerald-800'
            : r.status === 'rejected' ? 'bg-red-100 text-red-700'
            : r.status === 'cancelled' ? 'bg-slate-100 text-slate-600'
            : 'bg-amber-100 text-amber-800'}`}>
            {r.status === 'pending_hr' ? 'Pending HR/Admin' : r.status === 'pending' ? 'Pending' : (r.status || '').charAt(0).toUpperCase() + (r.status || '').slice(1)}
          </span>
        );
        return (
          <div className="space-y-3" role="tabpanel" id={`la-panel-${tab}`} aria-labelledby={`la-tab-${tab}`} data-testid={`approval-panel-${tab}`}>
            <FilterBar tab={tab} st={st} onChange={(patch) => updateTab(tab, patch)} onReset={() => updateTab(tab, { ...DEFAULT_TAB_STATE, pageSize: st.pageSize })}
              types={typeOptions.length ? typeOptions : null} statuses={statusOptions}
              searchPlaceholder={tab === 'cancellation' ? 'Search by employee name or reason...' : 'Search by employee name, type or reason...'} />

            <div key={panelKey} className="la-panel">
              <div className="la-table-wrap overflow-x-auto" data-tone={tone}>
                <table className="la-table w-full" data-testid={tab === 'pending' ? 'pending-requests-table' : tab === 'cancellation' ? 'cancellation-requests-table' : 'on-behalf-requests-table'}>
                  <thead>
                    {tab === 'pending' && <tr><th>Employee</th><th>Type</th><th>Dates</th><th>Days</th><th>Reason</th><th>Applied On</th><th className="text-right">Actions</th></tr>}
                    {tab === 'cancellation' && <tr><th>Employee</th><th>Type</th><th>Original Leave Dates</th><th>Charged Days</th><th>Cancellation Reason</th><th>Requested On</th><th className="text-right">Actions</th></tr>}
                    {tab === 'onbehalf' && <tr><th>Employee</th><th>Type</th><th>Dates</th><th>Days</th><th>Reason</th><th>Submitted By</th><th>Submitted On</th><th>Status</th><th className="text-right">Actions</th></tr>}
                  </thead>
                  {loading ? <TableSkeleton cols={cols} /> : (
                    <tbody>
                      {slice.length === 0 && (
                        <tr><td colSpan={cols}>
                          <div className="py-10 text-center" data-testid={`approval-empty-${tab}`}>
                            <CheckCircle className="w-9 h-9 text-emerald-500 mx-auto mb-2" />
                            <p className="text-slate-800 font-medium text-sm">{hasFilters ? 'No requests match your filters' : tab === 'pending' ? 'All caught up!' : tab === 'cancellation' ? 'No cancellation requests' : 'No on-behalf requests'}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{hasFilters ? 'Try adjusting or resetting the filters.' : 'Nothing requires your action here.'}</p>
                          </div>
                        </td></tr>
                      )}
                      {tab === 'pending' && slice.map(r => (
                        <tr key={r.id} data-testid={`pending-row-${r.id}`}>
                          <td><div className="flex items-center gap-2.5"><Avatar name={r.user_full_name} tone={tone} /><span className="font-medium text-slate-900">{r.user_full_name}</span>
                            {r.status === 'pending_hr' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">HR stage</span>}</div></td>
                          <td>{leaveTypeLabel(r.leave_type)}</td>
                          <td className="whitespace-nowrap">{shortRange(r.start_date, r.end_date)}</td>
                          <td data-testid={`pending-days-${r.id}`}>{r.total_days}{Number(r.sandwich_bridge_days) > 0 && <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold" title={`${r.base_days ?? (r.total_days - r.sandwich_bridge_days)} leave + ${r.sandwich_bridge_days} sandwich day(s) (${(r.sandwich_bridge_dates || []).join(', ')})`} data-testid={`sandwich-badge-${r.id}`}>+{r.sandwich_bridge_days} sandwich{r.sandwich_bridge_status === 'provisional' ? ' · prov.' : ''}</span>}</td>
                          <td className="max-w-[200px] truncate" title={r.reason}>{r.reason || '—'}</td>
                          <td className="text-slate-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
                          <td>{renderActions(r)}</td>
                        </tr>
                      ))}
                      {tab === 'cancellation' && slice.map(cr => (
                        <tr key={cr.id} data-testid={`cancellation-request-${cr.id}`}>
                          <td><div className="flex items-center gap-2.5"><Avatar name={cr.user_full_name} tone={tone} /><span className="font-medium text-slate-900">{cr.user_full_name}</span>
                            {cr.leave_started && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700" title="Leave already started — HR/Admin review required">Started</span>}</div></td>
                          <td>{leaveTypeLabel(cr.leave_type)}</td>
                          <td className="whitespace-nowrap">{shortRange(cr.start_date, cr.end_date)}</td>
                          <td>{cr.total_days}</td>
                          <td className="max-w-[200px] truncate" title={cr.cancellation_reason}>{cr.cancellation_reason || '—'}</td>
                          <td className="text-slate-500 whitespace-nowrap">{formatDate(cr.cancellation_requested_at)}</td>
                          <td>
                            <div className="flex items-center gap-1 justify-end">
                              <button onClick={() => setDetailsItem({ kind: 'cancellation', data: cr })} data-testid={`view-details-btn-${cr.id}`} title="View Details" className="la-action p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100"><Eye className="w-4 h-4" /></button>
                              <button onClick={() => setConfirmCancelApprove(cr)} disabled={cancelActionLoading === cr.id} data-testid={`approve-cancellation-btn-${cr.id}`} title="Approve Cancellation" className="la-action p-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"><CheckCircle className="w-4 h-4" /></button>
                              <button onClick={() => { setRejectCancelModal(cr); setRejectCancelReason(''); }} disabled={cancelActionLoading === cr.id} data-testid={`reject-cancellation-btn-${cr.id}`} title="Reject Cancellation" className="la-action p-1.5 rounded-lg text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-50"><XCircle className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {tab === 'onbehalf' && slice.map(r => (
                        <tr key={r.id} data-testid={`on-behalf-row-${r.id}`}>
                          <td><div className="flex items-center gap-2.5"><Avatar name={r.user_full_name} tone={tone} />
                            <div className="leading-tight"><span className="font-medium text-slate-900">{r.user_full_name}</span>
                              {r.backdated && <span className="block w-fit mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700" data-testid={`backdated-badge-${r.id}`}>Backdated</span>}</div></div></td>
                          <td>{leaveTypeLabel(r.leave_type)}</td>
                          <td className="whitespace-nowrap">{shortRange(r.start_date, r.end_date)}</td>
                          <td>{r.total_days}</td>
                          <td className="max-w-[160px] truncate" title={r.reason}>{r.reason || '—'}</td>
                          <td data-testid={`on-behalf-badge-${r.id}`}><span className="text-violet-800 font-medium">{r.on_behalf_submitted_by_name}</span></td>
                          <td className="text-slate-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
                          <td data-testid={`on-behalf-status-${r.id}`}>{statusChip(r)}</td>
                          <td>{renderActions(r)}</td>
                        </tr>
                      ))}
                    </tbody>
                  )}
                </table>
              </div>
            </div>

            <PaginationFooter tab={tab} st={st} total={filtered.length} page={page} totalPages={totalPages} onChange={(patch, resetPage) => updateTab(tab, patch, resetPage)} />
          </div>
        );
      })()}

      {/* Recent Leave Updates — compact, informational */}
      {recentUpdates.length > 0 && (
        <div className="pt-2" data-testid="recent-leave-updates-section">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-4 h-4 text-slate-400" />
            <h4 className="text-sm font-semibold text-slate-600">Recent Leave Updates</h4>
            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">{updatesTotal}</span>
            {!showAllUpdates && updatesTotal > 3 && (
              <button
                onClick={() => { setShowAllUpdates(true); fetchCancellationData(true); }}
                data-testid="view-all-updates-btn"
                className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-auto"
              >
                View All History
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {(showAllUpdates ? recentUpdates : recentUpdates.slice(0, 3)).map(u => {
              const wasApproved = u.cancellation_status === 'approved' || u.cancelled_stage === 'employee';
              return (
                <div key={u.id} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`leave-update-${u.id}`}>
                  <span className="font-medium text-slate-800">{u.user_full_name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${wasApproved ? 'bg-slate-200 text-slate-700' : 'bg-red-100 text-red-700'}`}>
                    {wasApproved ? 'Approved Leave Cancelled' : 'Cancellation Rejected'}
                  </span>
                  <span>{formatCalendarDate(u.start_date)} — {formatCalendarDate(u.end_date)} · {leaveTypeLabel(u.leave_type)}</span>
                  {wasApproved && Number(u.restored_days) > 0 && (
                    <span className="text-emerald-700">{u.restored_days} day(s) restored</span>
                  )}
                  <span className="text-slate-400">
                    {u.cancellation_decided_by_name ? `by ${u.cancellation_decided_by_name} · ` : ''}{formatDateTime(u.event_at)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ───────── Details Drawer ───────── */}
      {detailsItem && (() => {
        const d = detailsItem.data;
        const isCancellation = detailsItem.kind === 'cancellation';
        const span = weekdaySpan(d.start_date, d.end_date);
        const sandwichExtra = Number(d.total_days) > span.wk;
        const bridgeN = Number(d.sandwich_bridge_days) || 0;
        const ownN = typeof d.base_days === 'number' ? d.base_days : Number(d.total_days) - bridgeN;
        return (
          <div className="fixed inset-0 z-50" data-testid="details-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDetailsItem(null)} />
            <div className="absolute right-0 top-0 h-full w-full max-w-[480px] bg-white shadow-2xl overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
                <div>
                  <h4 className="text-base font-semibold text-slate-900">{d.user_full_name}</h4>
                  <p className="text-xs text-slate-500">
                    {isCancellation ? 'Cancellation Request' : d.submitted_on_behalf ? 'On-Behalf Leave Request' : 'Leave Request'}
                  </p>
                </div>
                <button onClick={() => setDetailsItem(null)} data-testid="close-drawer-btn"
                  className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-5 text-sm">
                {/* Chips */}
                <div className="flex flex-wrap gap-2">
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{leaveTypeLabel(d.leave_type)}</span>
                  {d.submitted_on_behalf && <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">On Behalf</span>}
                  {d.backdated && <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Backdated Leave Entry</span>}
                  {isCancellation && d.leave_started && <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Leave already started — HR/Admin only</span>}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-0.5">{isCancellation ? 'Original Leave Dates' : 'Leave Dates'}</p>
                    <p className="font-medium text-slate-800 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" />{formatCalendarDate(d.start_date)} — {formatCalendarDate(d.end_date)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-0.5">Day Part</p>
                    <p className="font-medium text-slate-800">{dayPartLabel(d.day_part)}</p>
                  </div>
                </div>

                {/* Charged days breakdown */}
                <div className="bg-slate-50 rounded-lg p-3" data-testid="charged-days-breakdown">
                  <p className="text-xs text-slate-400 mb-1.5">Charged Days Breakdown</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-lg font-bold text-slate-800">{span.cal}</p><p className="text-[11px] text-slate-500">Calendar span</p></div>
                    <div><p className="text-lg font-bold text-slate-800">{span.wk}</p><p className="text-[11px] text-slate-500">Weekdays</p></div>
                    <div><p className={`text-lg font-bold ${sandwichExtra ? 'text-amber-700' : 'text-slate-800'}`}>{d.total_days}</p><p className="text-[11px] text-slate-500">Charged</p></div>
                  </div>
                  {bridgeN > 0 && (
                    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="sandwich-cross-request-note">
                      <p className="font-semibold">Sandwich leave across requests ({d.sandwich_bridge_status === 'provisional' ? 'provisional' : 'confirmed'})</p>
                      <p className="mt-0.5">{ownN} leave day(s) + {bridgeN} intervening holiday/weekly-off day(s) ({(d.sandwich_bridge_dates || []).map(formatCalendarDate).join(', ')}) = {d.total_days} charged. The earlier adjacent request keeps its own days — nothing is counted twice.</p>
                      {d.sandwich_bridge_status === 'provisional' && <p className="mt-0.5 text-amber-700">Becomes final when both adjacent requests are approved; removed automatically if either is rejected or cancelled.</p>}
                    </div>
                  )}
                  {sandwichExtra && bridgeN === 0 && (
                    <p className="text-xs text-amber-700 mt-2 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Charged days exceed weekdays — includes sandwich-policy days</p>
                  )}
                  {Number(d.total_days) < span.wk && (
                    <p className="text-xs text-slate-500 mt-2">Charged days below weekdays — company holidays or half-day excluded</p>
                  )}
                </div>

                {/* Balance impact */}
                <div className={`rounded-lg p-3 border ${isCancellation ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`} data-testid="balance-impact">
                  <p className="text-xs text-slate-400 mb-0.5">Balance Impact</p>
                  <p className={`font-medium ${isCancellation ? 'text-emerald-800' : 'text-blue-800'} flex items-center gap-1.5`}>
                    {isCancellation
                      ? <><Undo2 className="w-4 h-4" /> Will restore {d.total_days} day(s) of {leaveTypeLabel(d.leave_type)} upon approval</>
                      : <><ShieldCheck className="w-4 h-4" /> Will deduct {d.total_days} day(s) of {leaveTypeLabel(d.leave_type)} upon approval</>}
                  </p>
                </div>

                {/* Reason */}
                <div>
                  <p className="text-xs text-slate-400 mb-1">{isCancellation ? 'Cancellation Reason' : 'Reason'}</p>
                  <p className="text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">
                    {(isCancellation ? d.cancellation_reason : d.reason) || '— none provided —'}
                  </p>
                </div>

                {/* Submission source */}
                <div>
                  <p className="text-xs text-slate-400 mb-1">Submission Source</p>
                  <p className="text-slate-700">
                    {isCancellation
                      ? <>Cancellation requested by <strong>{d.user_full_name}</strong> on {formatDateTime(d.cancellation_requested_at)}</>
                      : d.submitted_on_behalf
                        ? <>Submitted on behalf by <strong>{d.on_behalf_submitted_by_name}</strong> ({(d.on_behalf_submitted_by_role || '').replace(/_/g, ' ')}) on {formatDateTime(d.created_at)}</>
                        : <>Self-submitted by <strong>{d.user_full_name}</strong> on {formatDateTime(d.created_at)}</>}
                  </p>
                </div>

                {/* Documents */}
                {(d.documents || []).length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Supporting Documents</p>
                    <div className="space-y-1.5">
                      {d.documents.map(doc => (
                        <button key={doc.id} onClick={() => downloadDocument(doc)}
                          data-testid={`download-doc-${doc.id}`}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-blue-700 hover:bg-blue-50 text-left">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{doc.file_name || 'Document'}</span>
                          <Paperclip className="w-3.5 h-3.5 ml-auto text-slate-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Approval history / audit */}
                {(d.activity_trail || []).length > 0 && (
                  <div data-testid="approval-history">
                    <p className="text-xs text-slate-400 mb-1.5">Approval History & Audit</p>
                    <div className="space-y-2">
                      {d.activity_trail.map((t, i) => (
                        <div key={i} className="flex gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
                          <div className="text-xs">
                            <p className="text-slate-700">
                              <span className="font-semibold">{(t.action || '').replace(/_/g, ' ')}</span>
                              {' — '}{t.actor_name} ({(t.actor_role || '').replace(/_/g, ' ')})
                            </p>
                            <p className="text-slate-400">{formatDateTime(t.at)}</p>
                            {t.comment && <p className="text-slate-500 italic mt-0.5">&ldquo;{t.comment}&rdquo;</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Drawer actions */}
                <div className="pt-2 border-t border-slate-100">
                  {isCancellation ? (
                    <div className="flex gap-2">
                      <button onClick={() => setConfirmCancelApprove(d)} disabled={cancelActionLoading === d.id}
                        className="flex-1 px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                        Approve Cancellation
                      </button>
                      <button onClick={() => { setRejectCancelModal(d); setRejectCancelReason(''); }} disabled={cancelActionLoading === d.id}
                        className="flex-1 px-3 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50">
                        Reject Cancellation
                      </button>
                    </div>
                  ) : !isDecidableStatus(d) ? (
                    <p className="text-xs text-slate-500" data-testid="drawer-final-status-note">
                      This request has been {d.status} — no further action available.
                    </p>
                  ) : !canDecide(d) ? (
                    <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="drawer-maker-checker-note">
                      <Lock className="w-3.5 h-3.5" /> {lockReason(d)}
                    </p>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setCommentModal({ request: d, action: 'approve' }); setComment(''); }} disabled={actionLoading === d.id}
                        className="flex-1 px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                        Approve
                      </button>
                      <button onClick={() => { setRejectModal(d); setRejectionReason(''); }} disabled={actionLoading === d.id}
                        className="flex-1 px-3 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50">
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Approve confirmation (optional comment) */}
      {commentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="approve-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Approve Leave Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{commentModal.request.user_full_name}</strong> — {commentModal.request.total_days} day(s) {leaveTypeLabel(commentModal.request.leave_type)} leave
              ({formatCalendarDate(commentModal.request.start_date)} — {formatCalendarDate(commentModal.request.end_date)})
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comments (optional)</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={500}
              data-testid="approve-comment-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setCommentModal(null)} disabled={!!actionLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50">
                Back
              </button>
              <button onClick={() => handleAction(commentModal.request.id, 'approve')} disabled={!!actionLoading}
                data-testid="confirm-approve-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50">
                {actionLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Approving...</> : <><CheckCircle className="w-4 h-4" /> Confirm Approve</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject confirmation (reason required) */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="reject-confirm-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Leave Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{rejectModal.user_full_name}</strong> — {rejectModal.total_days} day(s) {leaveTypeLabel(rejectModal.leave_type)} leave
              ({formatCalendarDate(rejectModal.start_date)} — {formatCalendarDate(rejectModal.end_date)})
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rejection reason *</label>
            <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} rows={2} maxLength={500}
              placeholder="Required — visible to the employee"
              data-testid="reject-reason-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setRejectModal(null)} disabled={!!actionLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50">
                Back
              </button>
              <button onClick={() => handleAction(rejectModal.id, 'reject')} disabled={!!actionLoading || !rejectionReason.trim()}
                data-testid="confirm-reject-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
                {actionLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Rejecting...</> : <><XCircle className="w-4 h-4" /> Confirm Reject</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve-cancellation confirmation */}
      {confirmCancelApprove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="approve-cancellation-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Approve Cancellation?</h3>
            <p className="text-sm text-gray-600 mb-2">
              <strong>{confirmCancelApprove.user_full_name}</strong>&apos;s approved leave
              ({formatCalendarDate(confirmCancelApprove.start_date)} — {formatCalendarDate(confirmCancelApprove.end_date)}) will be cancelled and
              <strong> {confirmCancelApprove.total_days} day(s)</strong> restored to their balance.
            </p>
            <p className="text-xs text-gray-500 mb-4">This also removes the leave from calendars, availability and on-leave counts.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmCancelApprove(null)} disabled={!!cancelActionLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50">
                Back
              </button>
              <button onClick={() => handleCancellationDecision(confirmCancelApprove.id, 'approve')} disabled={!!cancelActionLoading}
                data-testid="confirm-approve-cancellation-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50">
                {cancelActionLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Approving...</> : <><CheckCircle className="w-4 h-4" /> Confirm</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject-cancellation modal (reason required) */}
      {rejectCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="reject-cancellation-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Cancellation Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{rejectCancelModal.user_full_name}</strong>&apos;s leave ({formatCalendarDate(rejectCancelModal.start_date)} — {formatCalendarDate(rejectCancelModal.end_date)}) will remain approved and no days will be restored.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rejection reason *</label>
            <textarea value={rejectCancelReason} onChange={(e) => setRejectCancelReason(e.target.value)} rows={2} maxLength={500}
              placeholder="Required — visible to the employee"
              data-testid="reject-cancellation-reason-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setRejectCancelModal(null)} disabled={!!cancelActionLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50">
                Back
              </button>
              <button onClick={() => handleCancellationDecision(rejectCancelModal.id, 'reject', rejectCancelReason)}
                disabled={!!cancelActionLoading || !rejectCancelReason.trim()}
                data-testid="confirm-reject-cancellation-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
                {cancelActionLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Rejecting...</> : <><XCircle className="w-4 h-4" /> Reject Cancellation</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Leave for Employee modal — LEAVE-ONBEHALF-BAL-001 */}
      <OnBehalfLeaveDialog open={onBehalfOpen} onClose={() => setOnBehalfOpen(false)} onSuccess={onBehalfSubmitted} />
    </div>
  );
};

export default LeaveApproval;
